import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { requestAll } from '../../read-all';
import type { AgentDashboardView } from '@forecastify/shared';
import { DashboardChat } from '../../agent/dashboard-chat';
import {
  contractEffortSchema,
  type ContractEffort,
  type EacMode,
  type Project,
  type SlipUnit,
} from '@forecastify/shared';
import { Button, DropdownMenu, Icon, LoadingOverlay, MultiSelect } from '@forecastify/ui';
import { useT } from '../../../i18n';
import { contractTypeLabel } from '../contract-type-label';
import { CONTRACT_TOPICS } from '../../../live/topics';
import { useLiveInvalidation } from '../../../live/use-live';
import { useSession } from '../../../session';
import { useWorkspaceNavigate } from '../../../workspace-path';
import { EffortKpis, FramePanel, EffortListPanel } from './effort-panels';
import { EffortChart, type Granularity } from './effort-chart';
import {
  EffortLineItemsTable,
  EffortPeriodsTable,
  EffortTasksTable,
  EffortResourcePanel,
} from './effort-tables';
import { EffortBurnCards } from './effort-burn';
import { deriveEffort } from './effort-model';
import { effectiveMode } from '../eac-mode';
import { lookbackMenuItems, type Lookback } from '../../history/use-lookback';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A projekt Dashboard fülje — C65. The hours twin of the Pénzügy tab.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS TAB EXISTED AND WAS THE WRONG SCREEN, WHICH IS WHY IT WAS MISSED.** Phase 4 REPLACES
 * the project's Dashboard rather than adding one, and `PHASE-4.md`'s inventory had headings only
 * for additions — new views, new tabs, new settings sections. A rebuild is none of those, so it
 * fell between the categories and never became a chunk. `PHASE-4.md` now has a fourth heading.
 *
 * ⚠️⚠️ **ONE CONTRACT — OR, SINCE U-4, THE WHOLE PROJECT.** A project may hold a retainer and a
 * fixed-price build at once, and one has a monthly frame and a daily burn while the other has a
 * deadline and a completion estimate — so the first control is still a contract picker. U-4 adds
 * *Teljes projekt* to it: every contract's frame, hours, deliverables and tasks at once (hours on
 * a task with no line item included), read as the `plain` variant with no periods, because two
 * contracts' periods do not line up. **A project with more than one contract opens on it**; one
 * with a single contract opens on that contract, where the two would be the same figures with the
 * periods taken away.
 *
 * ⚠️ **Scope and granularity are QUERIES; the rest is a reading.** Narrowing to a period changes
 * what every figure MEANS, so the server recomputes. The EAC formula and the slip unit change only
 * how one figure is spelled, so they stay here and cost no round trip.
 */
/**
 * ⚠️ **What the dashboard is scoped to, as three states rather than a nullable date.** `auto`
 * means *nobody has chosen* — the effect below turns it into the contract's current period, which
 * is what the mockup opens on. `whole` is a CHOICE, and the difference is why a bare `null` could
 * not carry this: the effect would drag a reader who picked *Teljes szerződés* straight back out
 * of it.
 */
/* 30 Oct 2026 · `periods` — one or SEVERAL, gaps allowed. One is the period view; several are
   the series view over just those. */
type Scope =
  { kind: 'auto' } | { kind: 'whole' } | { kind: 'periods'; startsOn: readonly string[] };

/** ⚠️ U-4 · the picker's extra entry, in the same state as a contract id. Never a uuid. */
const WHOLE_PROJECT = ':project';

export function ProjectEffortTab({
  project,
  asOf = null,
  lookback,
}: {
  readonly project: Project;
  /** C62.4 · ⚠️ the shared cut — this tab's kebab is how it is entered and left. */
  readonly lookback?: Lookback;
  /**
   * C62.1 · ⚠⚠ **THE CUT, PASSED STRAIGHT THROUGH TO THE SERVER.** `null` is live. The whole
   * panel travels because the server recomputes it from rewound rows — nothing here reconstructs
   * anything, which is the point of the temp-table design.
   */
  readonly asOf?: string | null;
}) {
  const t = useT();
  const session = useSession();
  const go = useWorkspaceNavigate();

  const [contracts, setContracts] = useState<
    readonly { id: string; identifier: string; name: string; type: string }[]
  >([]);
  const [contractId, setContractId] = useState<string | null>(null);
  const [view, setView] = useState<ContractEffort | null>(null);
  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C62.3 · `'auto'` IS WHY THE CARDS DIFFERED FROM THE MOCKUP.                │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * The mockup's own comment is one line: *"hatókör: ismétlődő szerződésen alapból az aktív
   * (vagy legutóbbi) időszak"* — and `phase5/02-project-dashboard.png` opens on `#8 · aug 1. – aug
   * 31.` because of it. We opened on the whole contract, which is a DIFFERENT CARD SET: five
   * figures about the series where the mockup shows five about the period. Round 6 item 4,
   * *"the cards on top of the dashboard, why are they different from the mockup?"*
   *
   * ⚠️ **Three states, not two, because *the whole contract* is a real choice.** A bare `null`
   * cannot tell *nobody has picked yet* from *the reader picked Teljes szerződés*, and the effect
   * below would drag them back into the current period every time they left it.
   */
  const [scope, setScope] = useState<Scope>({ kind: 'auto' });
  const [granularity, setGranularity] = useState<Granularity | null>(null);
  /* 30 Oct 2026 · EV · null until the reader picks — the server's default stands till then. */
  const [pickedEac, setEacMode] = useState<EacMode | null>(null);
  const eacMode = effectiveMode(pickedEac, view?.eac ?? null);
  const [slipUnit, setSlipUnit] = useState<SlipUnit>('workday');
  const [failed, setFailed] = useState(false);
  /* 30 Oct 2026 · which look-back day the figures on screen answer, and the newest request's number. */
  const seq = useRef(0);
  const [settledAsOf, setSettledAsOf] = useState<string | null | undefined>(undefined);
  const delta = lookback?.delta ?? false;
  const [liveView, setLiveView] = useState<ContractEffort | null>(null);

  const occurrenceStartsOn = scope.kind === 'periods' ? scope.startsOn : null;

  /* The project's contracts, for the first picker. Until they land the tab says it is loading
     rather than "no contract" — `use-first-load.ts`'s rule, by hand because `reload` waits on it. */
  const [contractsLoaded, setContractsLoaded] = useState(false);
  useEffect(() => {
    if (session.workspaceId === null) return;
    void (async () => {
      /* 31 Oct 2026 · the register pages now; this project's contracts are all of them. */
      const response = await requestAll(session, `/contracts?projectId=${project.id}`).catch(
        () => null,
      );
      if (response?.ok !== true) return;
      const body = (await response.json()) as { data?: unknown } | null;
      const rows = Array.isArray(body?.data) ? body.data : [];
      const mine = rows
        .filter(
          (
            one,
          ): one is {
            id: string;
            identifier: string;
            name: string;
            type: string;
            projectId: string;
          } =>
            typeof one === 'object' &&
            one !== null &&
            (one as { projectId?: unknown }).projectId === project.id,
        )
        .map((one) => ({ id: one.id, identifier: one.identifier, name: one.name, type: one.type }));
      setContracts(mine);
      /* ⚠️ U-4 · more than one contract opens on the whole project; one opens on itself. */
      setContractId(
        (current) => current ?? (mine.length > 1 ? WHOLE_PROJECT : (mine[0]?.id ?? null)),
      );
    })().finally(() => {
      setContractsLoaded(true);
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); depending on it refetches for ever. */
  }, [session.workspaceId, project.id]);

  const reload = useCallback(async (): Promise<void> => {
    if (contractId === null || session.workspaceId === null) return;
    const query = new URLSearchParams();
    if (occurrenceStartsOn !== null) query.set('occurrence', occurrenceStartsOn.join(','));
    /**
     * ⚠️⚠️ **`period` IS THE ABSENCE OF A GRANULARITY, WHICH IS WHY IT IS NOT SENT.** The
     * server buckets a whole retainer by period unless it is asked for a calendar, so asking for
     * nothing IS asking for periods. Sending the word would need a fourth value on the wire meaning
     * *the default*, and two ways to spell one state is how they drift apart.
     *
     * ⚠️ `null` likewise means *let the server pick from the span* — a month by day, a year by
     * month. A default sent from here would have to know that ladder in a second place.
     */
    if (granularity !== null && granularity !== 'period') query.set('granularity', granularity);

    /* C62.1 · the cut travels with every other parameter. Absent is live. */
    if (asOf !== null) query.set('asOf', asOf);
    /**
     * ⚠️⚠️ 30 Oct 2026 · **ONLY THE NEWEST ANSWER IS SHOWN.** Stepping through days fires a
     * request per click, and a slow one landing after a quicker, newer one put the wrong day's
     * figures under the scrubber. Each reload takes a number; an answer to an older number is
     * dropped. `settledAsOf` then says which day the figures on screen are for.
     */
    seq.current += 1;
    const mine = seq.current;
    const asked = asOf;

    const path = (params: URLSearchParams): string =>
      contractId === WHOLE_PROJECT
        ? `/projects/${project.id}/effort?${params.toString()}`
        : `/contracts/${contractId}/effort?${params.toString()}`;
    /**
     * 30 Oct 2026 · ⚠️ **ELTÉRÉS A JELENHEZ — TODAY'S FIGURES, READ ALONGSIDE THE PAST ONES.** The
     * kebab's toggle was wired to nothing; the mockup writes today's value under each tile, and
     * today's value is this same read without the cut.
     */
    const today = new URLSearchParams(query);
    today.delete('asOf');
    const [response, live] = await Promise.all([
      session.request(path(query)).catch(() => null),
      asOf !== null && delta ? session.request(path(today)).catch(() => null) : null,
    ]);
    const body = response?.ok === true ? ((await response.json()) as unknown) : null;
    const liveBody = live?.ok === true ? ((await live.json()) as unknown) : null;
    if (mine !== seq.current) return;
    setSettledAsOf(asked);
    const liveParsed = contractEffortSchema.safeParse(liveBody);
    setLiveView(liveParsed.success ? liveParsed.data : null);
    if (body === null) {
      setFailed(true);
      return;
    }
    const parsed = contractEffortSchema.safeParse(body);
    setFailed(!parsed.success);
    if (parsed.success) setView(parsed.data);
    /* ⚠️ `asOf` in the deps, or moving the scrubber would leave the panel showing the day
       before. */
  }, [session, contractId, project.id, occurrenceStartsOn, granularity, asOf, delta]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * ⚠️ **The pick happens once the periods are known, and then it is STICKY.** Storing the
   * chosen start rather than re-deriving it every render is what keeps this from fighting a reader
   * who steps back a period: after this runs, `scope` is a date and the guard never passes again.
   *
   * ⚠️ **Current, else the last closed one, else the first.** A retainer whose next period has
   * not started yet still has to open on something, and *the one that just ended* is the one
   * somebody looking at a dashboard means.
   */
  useEffect(() => {
    if (scope.kind !== 'auto' || view?.recurring !== true) return;
    const pick =
      view.periods.find((one) => one.state === 'current') ??
      view.periods.filter((one) => one.state === 'past').at(-1) ??
      view.periods[0];
    if (pick !== undefined) setScope({ kind: 'periods', startsOn: [pick.startsOn] });
  }, [scope, view]);

  useLiveInvalidation(CONTRACT_TOPICS, () => {
    void reload();
  });

  const model = useMemo(
    () => (view === null ? null : deriveEffort(view, { eacMode })),
    [view, eacMode],
  );
  const liveModel = useMemo(
    () => (liveView === null ? null : deriveEffort(liveView, { eacMode })),
    [liveView, eacMode],
  );

  /* No workspace is never going to answer, so it has nothing to wait for. */
  if (!contractsLoaded && session.workspaceId !== null) {
    return (
      <p className="text-muted-foreground p-6 text-center text-[12.5px]">{t('state.loading')}</p>
    );
  }

  if (contracts.length === 0) {
    return (
      <EmptyState
        label={t('effort.empty.contractLabel')}
        title={t('effort.empty.noContract')}
        body={t('effort.empty.noContractBody')}
      />
    );
  }

  const wholeProject = contractId === WHOLE_PROJECT;
  const contract = wholeProject
    ? null
    : (contracts.find((one) => one.id === contractId) ?? contracts[0]);
  const pickContract = (id: string): void => {
    setContractId(id);
    /* ⚠️ Both selections are CLEARED with the contract — a period start from the previous one
       narrows to nothing, and the screen reads as an empty contract. Back to `'auto'`, so the new
       contract opens on ITS current period. */
    setScope({ kind: 'auto' });
    setGranularity(null);
  };
  /* The picked periods, in series order; the trigger names them. */
  const pickedPeriods =
    view === null || occurrenceStartsOn === null
      ? []
      : view.periods.filter((one) => occurrenceStartsOn.includes(one.startsOn));
  const firstPicked = pickedPeriods[0];
  const scopeLabel =
    firstPicked === undefined
      ? t('effort.scope.whole')
      : pickedPeriods.length === 1
        ? `#${String(firstPicked.index)} · ${firstPicked.startsOn} – ${firstPicked.endsOn}`
        : pickedPeriods.length <= 3
          ? pickedPeriods.map((one) => `#${String(one.index)}`).join(', ')
          : t('finance.scope.periodsMany', { count: pickedPeriods.length });

  /* 19 Oct 2026 · AG-1 · what the dashboard chat is told the person is looking at. */
  const chatView: AgentDashboardView = {
    tab: 'overview',
    ...(contractId === null || contractId === WHOLE_PROJECT ? {} : { contractId }),
    /* The agent's tools read ONE period — with several it is told the whole contract. */
    ...(occurrenceStartsOn?.length !== 1 || occurrenceStartsOn[0] === undefined
      ? {}
      : { occurrenceStartsOn: occurrenceStartsOn[0] }),
    ...(asOf === null ? {} : { asOf }),
    eacMode,
    ...(granularity === 'day' || granularity === 'week' || granularity === 'month'
      ? { granularity }
      : {}),
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
      <div
        className="bg-background sticky top-0 z-30 flex flex-wrap items-center gap-2 border-b px-4 py-2"
        data-control-row
      >
        <DropdownMenu
          width={340}
          align="start"
          label={t('effort.pick.contract')}
          items={[
            /* ⚠️ U-4 · always offered, as on the finance tab — with one contract it also counts the
               project's hours outside any line item, which the contract view cannot. */
            {
              label: t('effort.scope.project'),
              description: t('effort.scope.projectBody'),
              checked: wholeProject,
              icon: 'layout-grid' as const,
              onSelect: () => {
                pickContract(WHOLE_PROJECT);
              },
            },
            { type: 'separator' as const },
            ...contracts.map((one) => ({
              label: `${one.identifier} · ${one.name}`,
              /**
               * ⚠️⚠️ **THE KIND, UNDER THE NAME — round 5 item 12.** *"Hiányzik hogy ismétlődő
               * vagy mérföldkő alapú, lásd mockup"*, and the mockup draws it as the second line of
               * each option. It is the fact that decides what the rest of this screen even offers:
               * a recurring contract has periods to scope by and a milestone one has none.
               */
              description: contractTypeLabel(t, one.type),
              checked: one.id === contract?.id,
              icon: 'file-text' as const,
              onSelect: () => {
                pickContract(one.id);
              },
            })),
          ]}
        >
          <Button variant="outline" size="sm" className="gap-1.5">
            <Icon
              name={wholeProject ? 'layout-grid' : 'file-text'}
              size={13}
              className="opacity-70"
            />
            {wholeProject ? t('effort.scope.project') : (contract?.identifier ?? '–')}
            {/* ⚠️⚠️ **EVERY KIND, NOT JUST THE RECURRING ONE.** The trigger named `Ismétlődő`
                and printed nothing at all for the other two — so a milestone contract looked like
                one whose kind had failed to load. Round 5 item 12. */}
            {wholeProject ? null : (
              <span className="text-muted-foreground text-[11px]">
                {contractTypeLabel(t, contract?.type ?? null)}
              </span>
            )}
            <Icon name="chevron-down" size={13} className="opacity-60" />
          </Button>
        </DropdownMenu>

        {view === null ? null : (
          <>
            {view.recurring ? (
              /* 30 Oct 2026 · several periods at once, as the money tab: a box per row, Shift+click
                 for a run, a search, and a list capped at the kit's height. */
              <div className="w-[280px] max-w-full">
                <MultiSelect
                  label={t('effort.pick.scope')}
                  placeholder={t('effort.scope.whole')}
                  options={view.periods.map((one) => ({
                    value: one.startsOn,
                    label: `#${String(one.index)} · ${one.startsOn} – ${one.endsOn}`,
                  }))}
                  values={occurrenceStartsOn ?? []}
                  onValuesChange={(values) => {
                    setScope(
                      values.length === 0
                        ? { kind: 'whole' }
                        : { kind: 'periods', startsOn: values },
                    );
                    setGranularity(null);
                  }}
                  checkboxes
                  rangeSelect
                  searchable
                  searchPlaceholder={t('finance.scope.periodSearch')}
                  emptyLabel={t('finance.scope.periodNone')}
                  hint={t('finance.scope.periodHint')}
                  clearLabel={t('effort.scope.whole')}
                  panelWidth={340}
                  summarize={() => (
                    <>
                      <Icon
                        name={firstPicked === undefined ? 'layout-grid' : 'calendar-days'}
                        size={13}
                        className="shrink-0 opacity-70"
                      />
                      <span className="truncate">{scopeLabel}</span>
                    </>
                  )}
                />
              </div>
            ) : null}

            <div className="flex-1" />

            <DropdownMenu
              width={260}
              align="end"
              label={t('effort.pick.more')}
              items={[
                /**
                 * ⚠️⚠️ **C62.4 · THE WAY INTO THE TIME MACHINE IS HERE, WHICH IS WHERE THE
                 * MOCKUP PUTS IT.** Its kebab is two menus: live, the way in; in the past, the way
                 * out and the delta — and every *open* command DISABLED with a reason, which is
                 * the half that answers *"if I click this now, which date does it act on?"*
                 */
                ...(lookback === undefined ? [] : lookbackMenuItems(t, lookback)),
                /* ⚠️ U-4 · the whole project IS no one contract to open — its rows each open
                   their own. */
                ...(view.wholeProject
                  ? []
                  : [
                      {
                        type: 'separator' as const,
                      },
                      {
                        label: t('effort.action.openContract'),
                        icon: 'file-text' as const,
                        disabled: lookback?.onDate !== undefined && lookback.onDate !== null,
                        ...(lookback?.onDate === undefined || lookback.onDate === null
                          ? {}
                          : { description: t('tt.lockedAction') }),
                        onSelect: () => {
                          go(`/contracts/${view.contractIdentifier}`);
                        },
                      },
                    ]),
              ]}
            >
              <Button variant="ghost" size="icon-sm" aria-label={t('effort.pick.more')}>
                <Icon name="ellipsis" size={15} />
              </Button>
            </DropdownMenu>
          </>
        )}
      </div>

      {/* 30 Oct 2026 · the body under one roof, so a picked day's scrim covers all of it. */}
      <div className="relative flex flex-col">
        <LoadingOverlay
          show={view !== null && settledAsOf !== undefined && settledAsOf !== asOf}
          label={asOf === null ? t('tt.loadingLive') : t('tt.loading', { date: asOf.slice(0, 10) })}
        />
        {failed ? (
          <p className="text-destructive px-4 pt-3 text-[12px]">{t('error.unavailable')}</p>
        ) : null}

        {view === null || model === null ? (
          <p className="text-muted-foreground p-6 text-center text-[12.5px]">
            {t('state.loading')}
          </p>
        ) : view.lineItems.length === 0 ? (
          <EmptyState
            label={t('effort.empty.lineItemLabel')}
            title={t('effort.empty.noLineItem')}
            body={t('effort.empty.noLineItemBody')}
          />
        ) : (
          <div className="flex flex-col gap-4 p-4">
            <EffortKpis
              view={view}
              model={model}
              slipUnit={slipUnit}
              onSlipUnit={setSlipUnit}
              live={
                asOf !== null && delta && liveView !== null && liveModel !== null
                  ? { view: liveView, model: liveModel }
                  : null
              }
            />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
              <FramePanel model={model} eac={view.eac} eacMode={eacMode} onEacMode={setEacMode} />
              <EffortListPanel view={view} model={model} />
            </div>

            <EffortChart
              view={view}
              granularity={granularity ?? defaultGranularity(view)}
              onGranularity={setGranularity}
            />

            {/* 30 Oct 2026 · burn-down and burn-up, side by side, before the deliverables. */}
            <EffortBurnCards view={view} />

            {/**
             * ⚠️⚠️ **BOTH REGISTERS, IN THIS ORDER: WHAT WAS PROMISED, THEN HOW EACH PERIOD WENT.**
             * One component used to pick between them, so a retainer seen whole showed its periods
             * and hid the deliverables — the rows the frame above is the sum of. The mockup draws
             * both, and a period table with no deliverables beside it cannot answer *where did the
             * frame come from*.
             */}
            <EffortLineItemsTable
              view={view}
              model={model}
              onOpenLineItem={(id) => {
                const item = view.lineItems.find((one) => one.id === id);
                /* ⚠️ The item's OWN contract — on the whole project they are not all one. */
                if (item !== undefined)
                  go(`/contracts/${item.contractIdentifier}/${item.identifier}`);
              }}
            />

            {view.recurring && view.selectedPeriod === null ? (
              <EffortPeriodsTable
                view={view}
                model={model}
                onOpenPeriod={(startsOn) => {
                  /* ⚠️ A period row NARROWS the dashboard rather than leaving it — the mockup's own
                   behaviour, and why the scope picker and this row share one state. */
                  setScope({ kind: 'periods', startsOn: [startsOn] });
                  setGranularity(null);
                }}
              />
            ) : null}

            <EffortTasksTable
              view={view}
              model={model}
              onOpenTask={(id) => {
                const task = view.tasks.find((one) => one.id === id);
                if (task !== undefined) go(`/tasks/${task.key}`);
              }}
            />

            <EffortResourcePanel view={view} model={model} />
          </div>
        )}
      </div>

      <DashboardChat projectId={project.id} view={chatView} />
    </div>
  );
}

/**
 * ⚠️ **What the server did when it was asked for nothing, so the control can show it.** The
 * request sends no granularity until somebody picks one; without this the switcher would open with
 * none of its options lit while the chart was plainly drawn by one of them.
 */
function defaultGranularity(view: ContractEffort): Granularity {
  /* ⚠️ U-4 · the server buckets the whole project by calendar month. */
  if (view.wholeProject) return 'month';
  if (view.recurring && view.selectedPeriod === null) return 'period';
  const span =
    Math.round(
      (new Date(`${view.to}T00:00:00Z`).getTime() - new Date(`${view.from}T00:00:00Z`).getTime()) /
        86_400_000,
    ) + 1;
  return span <= 32 ? 'day' : span <= 200 ? 'week' : 'month';
}

function EmptyState({
  label,
  title,
  body,
}: {
  readonly label: string;
  readonly title: string;
  readonly body: string;
}) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center p-8">
      <div className="max-w-[420px] text-center">
        <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {label}
        </div>
        <div className="mt-1 text-[32px] leading-none font-medium tabular-nums">0</div>
        <div className="mt-3 text-[13px] font-medium">{title}</div>
        <p className="text-muted-foreground mt-1 text-[12.5px] leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
